import React from 'react';
import { Video } from '../partials/Video';
import { asView } from '../../../_lib/ViewFetcher';
import { useBlockContext } from '../../../_lib/BlockFetcher';
import { ContentVideoLayout } from '../VideoBlock.types';
import { useSonTranslation } from '../../../translation/useSonTranslation';
import { useWidgetContext } from '../../../_lib/components/Widget';
import { WidgetType } from '../../../types/api/ElementType';
import bem from '../../../lib/bemUtils/bem';
import { ContentBlockHeadline, ContentBlockHeadlineModifier } from '../../../_lib/components/ContentBlockHeadline';
import { Namespace } from '../../../translation/namespace';
import { isNotNullOrUndefined } from '../../../lib/typeGuards';

type ContentVideo = {
  title: string;
  caption: string;
  copyright: string;
  source: string;
  contentType: 'video/mp4';
  captionsFile: string;
  layout: ContentVideoLayout;
} & Video;

const mapLayoutToModifier = (layout: ContentVideoLayout) => {
  switch (layout) {
    case 'largeVideoDescriptionBelow':
      return 'large-video-description-below';
    case 'mediumVideoDescriptionBelow':
      return 'medium-video-description-below';
    case 'smallVideoDescriptionBelow':
      return 'small-video-description-below';
    case 'mediumVideoDescriptionRight':
      return 'medium-video-description-right';
    case 'smallVideoDescriptionRight':
      return 'small-video-description-right';
    default:
      return undefined;
  }
};

export function ContentVideo({ title, source, contentType, captionsFile, caption, copyright, layout }: ContentVideo) {
  const { t } = useSonTranslation('Video');
  const { widgetType } = useWidgetContext();
  const { uuid } = useBlockContext();

  if (!source) {
    return null;
  }

  const modifiers = [];

  // gray background modfiers & different text style for top videos on the home page
  const isTopVideo = widgetType === WidgetType.topvideo;

  if (isTopVideo) {
    modifiers.push('topvideo');
  }

  modifiers.push(mapLayoutToModifier(layout));

  return (
    <div className={bem('content-video', modifiers.filter(isNotNullOrUndefined))}>
      <ContentBlockHeadline
        // header is <h2>
        // editModeInfo.active blockinteraction is <h3>
        // depending on them, hierarchy is <h2>, <h3> or <h4>
        hierarchy={2}
        title={title}
        modifier={isTopVideo ? ContentBlockHeadlineModifier.gray : null}
      />
      <figure className="content-video__figure">
        <div className="content-video__wrapper">
          <Video
            source={source}
            captionsFile={captionsFile}
            contentType={contentType}
            ariaDescribedby={`content-video-${uuid}`}
          />
          <p className="content-video__copyright" id={`content-video-${uuid}`}>
            <dfn className="invisible">{`${t(`${Namespace.glossary}:copyright`)}:`}</dfn>
            <span aria-hidden="true">©</span> {copyright}
          </p>
        </div>
        {caption && (
          <figcaption className="content-video__figcaption">
            <p>{caption}</p>
          </figcaption>
        )}
      </figure>
    </div>
  );
}

export const VideoDefaultView = asView(ContentVideo);
